import { doc, setDoc, getDocs, collection, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logger } from '../utils/logger';

export interface Product {
  id: string;
  name: string;
  industry: string | string[]; // Support both single and multiple industries for backward compatibility
  price: string;
  warranty: boolean;
  image: string;
  createdAt: Date;
  category?: 'pos-systems' | 'mobile-solutions' | 'online-payments' | 'handheld-terminals';
  description?: string;
  features?: string[];
  specs?: Record<string, string>;
  rating?: number;
  reviews?: number;
  originalPrice?: string | null;
  type?: string; // Add type field for compatibility
  actionType?: 'buy' | 'quote'; // Determines if button shows "Buy" or "Get Quote"
}

// Save product to Firestore
export const saveProduct = async (product: Omit<Product, 'id' | 'createdAt'>) => {
  try {
    const productData = {
      ...product,
      createdAt: new Date(),
    };
    
    const docRef = doc(collection(db, 'products'));
    await setDoc(docRef, productData);
    
    logger.log('Product saved to Firestore:', docRef.id);
    return { id: docRef.id, ...productData };
  } catch (error) {
    logger.error('Error saving product:', error);
    throw error;
  }
};

// Get all products from Firestore
export const getProducts = async (): Promise<Product[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'products'));
    const products: Product[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      products.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
      } as Product);
    });
    
    logger.log('Products loaded from Firestore:', products.length);
    return products;
  } catch (error) {
    logger.error('Error getting products:', error);
    return [];
  }
};

// Update product in Firestore
export const updateProduct = async (productId: string, product: Partial<Product>) => {
  try {
    const productRef = doc(db, 'products', productId);
    await setDoc(productRef, product, { merge: true });
    
    logger.log('Product updated in Firestore:', productId);
    return true;
  } catch (error) {
    logger.error('Error updating product:', error);
    throw error;
  }
};

// Delete product from Firestore
export const deleteProduct = async (productId: string) => {
  try {
    await deleteDoc(doc(db, 'products', productId));
    logger.log('Product deleted from Firestore:', productId);
    return true;
  } catch (error) {
    logger.error('Error deleting product:', error);
    throw error;
  }
};
